import type { ResolvedShopDomain } from '~/utils/shopDomain'
import { isPlatformHost, normalizeClientHost, platformRootDomain } from '~/utils/shopDomain'

type DomainState = {
  host: string
  shop: ResolvedShopDomain | null
  resolved: boolean
}

function currentHost(): string {
  if (import.meta.server) {
    try {
      return normalizeClientHost(useRequestURL().host)
    } catch {
      return ''
    }
  }
  if (typeof window !== 'undefined') {
    return normalizeClientHost(window.location.host)
  }
  return ''
}

/**
 * Resolve whether the current Host is a shop custom domain or slug.novartshop.ro.
 * Cached in useState for SSR + client hydration.
 */
export async function ensureShopDomainResolved(): Promise<DomainState> {
  const state = useState<DomainState>('shop-domain', () => ({
    host: '',
    shop: null,
    resolved: false,
  }))

  const host = currentHost()
  if (!host) {
    state.value = { host: '', shop: null, resolved: true }
    return state.value
  }

  if (state.value.resolved && state.value.host === host) {
    return state.value
  }

  if (isPlatformHost(host, platformRootDomain())) {
    state.value = { host, shop: null, resolved: true }
    return state.value
  }

  const config = useRuntimeConfig()
  const platformApi = String(config.public.platformApiUrl || '').replace(/\/$/, '')

  try {
    const res = await $fetch<{ success: boolean, data: ResolvedShopDomain }>(
      `${platformApi}/resolve-host`,
      { query: { host } },
    )

    if (res?.success && res.data?.slug) {
      state.value = { host, shop: res.data, resolved: true }
    } else {
      state.value = { host, shop: null, resolved: true }
    }
  } catch {
    state.value = { host, shop: null, resolved: true }
  }

  return state.value
}

export function useShopDomain() {
  const state = useState<DomainState>('shop-domain', () => ({
    host: '',
    shop: null,
    resolved: false,
  }))

  const customDomainMode = computed(() => Boolean(state.value.shop?.slug))
  const domainShopSlug = computed(() => state.value.shop?.slug || '')
  const domainShop = computed(() => state.value.shop)

  return {
    state,
    customDomainMode,
    domainShopSlug,
    domainShop,
    ensureShopDomainResolved,
  }
}
